import { FC, ReactNode, createContext, useContext, useState } from 'react';

interface WebsocketProps {
  children?: ReactNode;
}

type OnMessage = (update: any) => void;

export type PolarisWebSocket = {
  socket: WebSocket;
  onMessage: Record<string, OnMessage>;
  onClose?: () => void;
};

export interface WebSocketContextType {
  sockets: Record<string, PolarisWebSocket>;
  createSocket: (id: string, url: string, onMessage: (update: any) => void, onClose?: () => void) => PolarisWebSocket;
  getSocket: (id: string) => PolarisWebSocket;
  removeSocket: (id: string) => void;
  addMessageToSocket: (socketId: string, messageId: string, newOnMessage: (update: any) => void) => void;
  removeMessageFromSocket: (socketId: string, messageId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider: FC<WebsocketProps> = ({ children = <></> }) => {
  const [sockets, setSockets] = useState<Record<string, PolarisWebSocket>>({});

  const createSocket = (id: string, url: string, onMessage: (update: any) => void, onClose?: () => void) => {
    const token = localStorage.getItem('token');
    const { host, protocol } = window.location;
    const wsType = protocol === 'https:' ? 'wss' : 'ws';

    if (!sockets[id]) {
      const newSocket = new WebSocket(`${wsType}://${host.replace('3000', '8000')}${url}?token=${token}`);
      const updatedSockets: Record<string, PolarisWebSocket> = {
        ...sockets,
        [id]: {
          socket: newSocket,
          onMessage: {
            original: onMessage
          } as Record<string, OnMessage>,
          onClose: onClose
        }
      };

      setSockets(updatedSockets);

      newSocket.onopen = () => {
        console.log(`opened socket ${id}`);
        return updatedSockets[id];
      };

      newSocket.onmessage = (event) => {
        const update = JSON.parse(event.data);
        const keys = Object.keys(updatedSockets[id].onMessage);

        keys.forEach((key) => {
          updatedSockets[id].onMessage[key](update);
        });
      };

      newSocket.onclose = (event) => {
        console.log(`closed socket ${id}`, event.code, event.reason);
      };

      newSocket.onerror = (error) => {
        console.error('socket error:', error);
      };

      return updatedSockets[id];
    } else {
      return sockets[id];
    }
  };

  const addMessageToSocket = (socketId: string, messageId: string, newOnMessage: OnMessage) => {
    if (!sockets[socketId]) {
      console.log('no socket to add message to');
      return;
    }

    setSockets((prevSockets) => {
      const newSockets = { ...prevSockets };
      newSockets[socketId].onMessage[messageId] = newOnMessage;
      return newSockets;
    });
    console.log('added new onMessage to socket:', sockets[socketId]);
  };

  const removeMessageFromSocket = (socketId: string, messageId: string) => {
    if (!sockets[socketId] || !sockets[socketId].onMessage[messageId]) {
      console.log(
        !sockets[socketId]
          ? 'no socket to remove message from'
          : !sockets[socketId].onMessage[messageId]
          ? 'no message to remove from socket'
          : 'wut - no'
      );
      return;
    }

    setSockets((prevSockets) => {
      const newSockets = { ...prevSockets };
      delete newSockets[socketId].onMessage[messageId];
      return newSockets;
    });
  };

  const getSocket = (id: string) => {
    return sockets[id];
  };

  const removeSocket = (id: string) => {
    if (!sockets[id]) {
      return;
    }

    const socket = sockets[id];
    if (socket?.onClose) {
      socket.onClose();
    }

    setSockets((prevSockets) => {
      const newSockets = { ...prevSockets };
      delete newSockets[id];
      console.log('removed socket:', id);
      return newSockets;
    });
  };

  return (
    <WebSocketContext.Provider value={{ sockets, getSocket, createSocket, addMessageToSocket, removeMessageFromSocket, removeSocket }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebsocketContext = () => useContext(WebSocketContext);
